/**
 * Open a table artifact in its own browser window.
 *
 * WHY A WINDOW AND NOT A BIGGER MODAL: a 200-row table is something you park
 * next to the conversation and refer back to — sorting it in a modal that closes
 * when you click away defeats the purpose. A real window can be moved to a
 * second monitor, kept open, and printed.
 *
 * SECURITY — the reason this is a module and not three lines inline: a new
 * window opened from `window.open()` is SAME-ORIGIN. Writing model-authored
 * table content into it with `document.write` or `innerHTML` would be a
 * self-XSS on hub.westtn.consulting with full session access. So every value
 * reaches the document through `textContent` / `createTextNode` on nodes built
 * with `createElement` — there is no string-to-HTML path here at all. The only
 * `innerHTML`-ish thing is the stylesheet, which is a constant we author.
 */

import type { ArtifactData } from '../../lib/artifacts/types';
import { formatTableCell, type ParsedArtifactTable } from './artifact-utils';
import { describeColumns } from './artifact-table-utils';

/** Stylesheet for the standalone window. A constant — never interpolated with data. */
const WINDOW_STYLES = `
  :root { color-scheme: light dark; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 24px;
    font: 14px/1.5 system-ui, -apple-system, "Segoe UI", sans-serif;
    color: #111827;
    background: #ffffff;
  }
  h1 { font-size: 18px; margin: 0 0 4px; }
  p.meta { margin: 0 0 16px; color: #6b7280; font-size: 12px; }
  .scroller { overflow-x: auto; border: 1px solid #e5e7eb; border-radius: 8px; }
  table { border-collapse: separate; border-spacing: 0; width: 100%; font-variant-numeric: tabular-nums; }
  thead th {
    position: sticky; top: 0; z-index: 1;
    background: #f9fafb; text-align: left; font-weight: 600;
    padding: 10px 12px; border-bottom: 1px solid #e5e7eb; white-space: nowrap;
  }
  tbody td { padding: 8px 12px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
  tbody tr:nth-child(even) { background: #fafafa; }
  tbody tr:last-child td { border-bottom: none; }
  td.num, th.num { text-align: right; }
  td.rownum, th.rownum { color: #9ca3af; text-align: right; width: 1%; white-space: nowrap; }
  @media print { body { padding: 0; } .scroller { border: none; } }
  @media (prefers-color-scheme: dark) {
    body { color: #e5e7eb; background: #0b0f19; }
    p.meta { color: #9ca3af; }
    .scroller { border-color: #1f2937; }
    thead th { background: #111827; border-bottom-color: #1f2937; }
    tbody td { border-bottom-color: #111827; }
    tbody tr:nth-child(even) { background: #0f1420; }
    td.rownum, th.rownum { color: #6b7280; }
  }
`;

export type OpenTableWindowResult = 'opened' | 'blocked';

/**
 * Render a parsed table into a fresh window. Returns `'blocked'` when the popup
 * blocker refused, so the caller can tell the user rather than appearing to do
 * nothing.
 */
export function openTableInNewWindow(
  artifact: Pick<ArtifactData, 'title'>,
  table: ParsedArtifactTable,
): OpenTableWindowResult {
  const win = window.open('', '_blank', 'noopener,noreferrer,width=1100,height=800');
  if (!win) return 'blocked';

  const doc = win.document;
  const columns = describeColumns(table);

  // `title` is set as a property, not written as markup — it is the one place a
  // model-authored string reaches the browser chrome.
  doc.title = artifact.title;

  const style = doc.createElement('style');
  style.textContent = WINDOW_STYLES;
  doc.head.appendChild(style);

  const heading = doc.createElement('h1');
  heading.textContent = artifact.title;
  doc.body.appendChild(heading);

  const meta = doc.createElement('p');
  meta.className = 'meta';
  meta.textContent = `${table.rows.length} ${table.rows.length === 1 ? 'row' : 'rows'} · ${
    table.columns.length
  } ${table.columns.length === 1 ? 'column' : 'columns'}`;
  doc.body.appendChild(meta);

  const scroller = doc.createElement('div');
  scroller.className = 'scroller';

  const tableEl = doc.createElement('table');

  const thead = doc.createElement('thead');
  const headRow = doc.createElement('tr');
  const rowNumHead = doc.createElement('th');
  rowNumHead.className = 'rownum';
  rowNumHead.textContent = '#';
  headRow.appendChild(rowNumHead);
  for (const column of columns) {
    const th = doc.createElement('th');
    if (column.numeric) th.className = 'num';
    th.textContent = column.key;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  tableEl.appendChild(thead);

  const tbody = doc.createElement('tbody');
  table.rows.forEach((row, index) => {
    const tr = doc.createElement('tr');
    const rowNum = doc.createElement('td');
    rowNum.className = 'rownum';
    rowNum.textContent = String(index + 1);
    tr.appendChild(rowNum);
    for (const column of columns) {
      const td = doc.createElement('td');
      if (column.numeric) td.className = 'num';
      td.textContent = formatTableCell(row[column.key]);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  });
  tableEl.appendChild(tbody);

  scroller.appendChild(tableEl);
  doc.body.appendChild(scroller);

  return 'opened';
}
